import { memo, useEffect, useMemo, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useGetUserLiveSetting } from '../../../../hooks/queries/setting/use-get-user-setting'
import { useGlobalStore } from '../../../../store/global-store'
import type { LiveChat } from '../../../../types/lives/live'
import { Message } from './components/message'
import { MessageSupport } from './components/message-support'
import { useMessages } from './use-messages'

interface Props {
  filter: string
}

const MOCK_LEVELS = [5, 15, 25, 35, 45, 55, 65, 75, 85, 95]
const MOCK_VIPS = [1, 2, 3, 4, 5, 6]
const MOCK_AGENCY_LEVELS = [1]
const MOCK_RESELLER_LEVELS = [1, 2, 3]

function createMockChat(
  id: string,
  username: string,
  message: string,
  overrides: Partial<LiveChat> = {},
): LiveChat {
  const avatarName = encodeURIComponent(username)

  return {
    id,
    isInvisible: false,
    colors: {},
    message: {
      message,
      type: 'chat',
      userSentId: Number(id.replace(/\D/g, '') || '1'),
    } as LiveChat['message'],
    user: {
      id: Number(id.replace(/\D/g, '') || '1'),
      username,
      imgUser: `https://ui-avatars.com/api/?name=${avatarName}&background=1f2937&color=ffffff&size=96&bold=true`,
    } as LiveChat['user'],
    ...overrides,
  }
}

function createMockBadgeMessages(): LiveChat[] {
  const levelMessages = MOCK_LEVELS.map((level) =>
    createMockChat(
      `mock-level-${level}`,
      `Level ${level}`,
      `Preview badge de level ${level}`,
      {
        level: {
          id: `level-${level}`,
          level: String(level),
          gradientColor: ['#4568DC', '#B06AB3'],
        },
      },
    ),
  )

  const vipMessages = MOCK_VIPS.map((vipId) =>
    createMockChat(`mock-vip-${vipId}`, `VIP ${vipId}`, `Preview badge VIP ${vipId}`, {
      userVip: {
        id: vipId,
        vipId,
        vip: {
          id: vipId,
          vipLevel: vipId,
          iconVip: '',
        },
      },
    }),
  )

  const agencyMessages = MOCK_AGENCY_LEVELS.map((agencyLevel) =>
    createMockChat(
      `mock-agency-${agencyLevel}`,
      `Agency ${agencyLevel}`,
      `Preview badge de agencia ${agencyLevel}`,
      { agencyBadges: agencyLevel },
    ),
  )

  const resellerMessages = MOCK_RESELLER_LEVELS.map((resellerLevel) =>
    createMockChat(
      `mock-reseller-${resellerLevel}`,
      `Reseller ${resellerLevel}`,
      `Preview badge de reseller ${resellerLevel}`,
      { resellerBadges: resellerLevel },
    ),
  )

  const specialMessages = [
    createMockChat(
      'mock-lucky-hero',
      'Lucky Hero',
      'Preview badge lucky hero',
      { luckyHero: true },
    ),
    createMockChat(
      'mock-super-rich',
      'Super Rich',
      'Preview badge super rich',
      { superRich: true },
    ),
    createMockChat(
      'mock-combo-all',
      'Combo Max',
      'Preview com varias badges combinadas',
      {
        level: {
          id: 'level-combo',
          level: '95',
          gradientColor: ['#4568DC', '#B06AB3'],
        },
        userVip: {
          id: 6,
          vipId: 6,
          vip: {
            id: 6,
            vipLevel: 6,
            iconVip: '',
          },
        },
        agencyBadges: 5,
        resellerBadges: 3,
        luckyHero: true,
        superRich: true,
      },
    ),
  ]

  return [
    ...levelMessages,
    ...vipMessages,
    ...agencyMessages,
    ...resellerMessages,
    ...specialMessages,
  ]
}

export const Messages = memo(({ filter }: Props) => {
  useMessages()

  const containerRef = useRef<HTMLDivElement>(null)

  const { setting } = useGetUserLiveSetting()

  const { chat, clearChatLive } = useGlobalStore(
    useShallow((state) => ({
      chat: state.chat,
      clearChatLive: state.clearChatLive,
    })),
  )

  const shouldMockBadges = useMemo(() => {
    const params = new URLSearchParams(window.location.search)

    return (
      params.get('mockBadges') === '1' || params.get('previewBadges') === '1'
    )
  }, [])

  const mockedBadgeMessages = useMemo(() => {
    if (!shouldMockBadges) {
      return []
    }

    return createMockBadgeMessages()
  }, [shouldMockBadges])

  const allChat = useMemo(() => {
    if (!setting?.playNotificationGiftSentPushLive) {
      return chat?.filter((chat) => chat?.user?.id)
    }

    return chat
  }, [chat, setting?.playNotificationGiftSentPushLive])

  const messagesChat = useMemo(() => {
    const realMessages = chat?.filter((chat) => chat?.user?.id) ?? []

    return [...mockedBadgeMessages, ...realMessages]
  }, [chat, mockedBadgeMessages])

  const messagesGift = useMemo(() => {
    return chat?.filter((chat) => !chat?.user?.id)
  }, [chat])

  const currentChat = useMemo(
    () => ({
      all: [...mockedBadgeMessages, ...(allChat ?? [])],
      gifts: messagesGift,
      chat: messagesChat,
    }),
    [allChat, messagesGift, messagesChat, mockedBadgeMessages],
  )

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    if (chat?.length >= 100) {
      clearChatLive()
    }

    const lastMessage = containerRef.current.lastChild as Element

    lastMessage?.scrollIntoView({ behavior: 'smooth' })
  }, [chat, clearChatLive])

  return (
    <div ref={containerRef} className="flex flex-col gap-2 overflow-y-scroll pt-2">
      <MessageSupport />

      {currentChat[filter as keyof typeof currentChat]?.map((chat) => (
        <Message key={chat.id} chat={chat} />
      ))}
    </div>
  )
})
